// src/components/resources/calculators/UgcCostCalculator.related.ts

/**
 * Hand-curated cross-links for the UGC Cost Calculator's related section.
 *
 * Kept in a sibling (non-`'use client'`) module so the slugs can be
 * unit-tested against the live glossary + calculator registries
 * (`src/lib/resources/__tests__/related-link-slugs.test.ts`) without
 * pulling the calculator's client-only React deps into the test runtime.
 */

export interface RelatedLinkEntry {
  /** Slug — for glossary: `<category>/<slug>`; for calculators: bare slug. */
  slug: string
  /** Short link text. */
  label: string
  /** One-line explanatory note rendered after the link. */
  note: string
}

export const RELATED_GLOSSARY: ReadonlyArray<RelatedLinkEntry> = [
  {
    slug: 'creative/user-generated-content-ugc',
    label: 'User-Generated Content (UGC)',
    note: 'What UGC is and why it outperforms polished brand ads on paid social.',
  },
  {
    slug: 'metrics/thumbstop-rate-tsr',
    label: 'Thumbstop Rate',
    note: 'The creative metric that moves first when a UGC slate is working.',
  },
  {
    slug: 'metrics/hold-rate',
    label: 'Hold Rate',
    note: 'Read hook rate next to hold rate before you scale a winner.',
  },
  {
    slug: 'metrics/customer-acquisition-cost-cac',
    label: 'CAC',
    note: 'Cost-per-winner only matters relative to your acquisition economics.',
  },
] as const

export const RELATED_TOOLS: ReadonlyArray<RelatedLinkEntry> = [
  {
    slug: 'creative-testing-calculator',
    label: 'Creative Testing Calculator',
    note: 'Size the media budget needed to validate each UGC concept.',
  },
  {
    slug: 'marketing-budget-calculator',
    label: 'Marketing Budget Calculator',
    note: 'Allocate the production line inside your broader channel mix.',
  },
  {
    slug: 'cpm-calculator',
    label: 'CPM Calculator',
    note: 'Convert a UGC testing slate into expected impression cost.',
  },
  {
    slug: 'roas-calculator',
    label: 'ROAS Calculator',
    note: 'Check whether winners clear your return target after production cost.',
  },
] as const
